import { freezeTargets } from "../domain/cover";
import { todayStr } from "../domain/date";
import { entriesFor, isLogged, loggedIdFor, planFor } from "../domain/intake";
import { ranFor } from "../domain/queue";
import { gentleOffKey } from "../domain/week";
import { nextDose, phasePinFor, programmeOf, type DoseDecision, type PhasePinDecision } from "../domain/readiness";
import type {
  CustomFood,
  DayRan,
  DayTests,
  EatingAnswers,
  EatingProfile,
  FoodEntry,
  HowWas,
  KayaState,
  MealPlan,
  MealPlanDay,
  MealSlot,
  PlanItem,
  SessionKey,
  TrainingAnswers,
  TrainingProfile,
  WaterEntry,
  WaterKind,
  WaterSettings,
} from "../domain/types";
import { WATER_DEFAULTS, drinkIsNew, type PendingDrink } from "../domain/water";
import {
  SOAKED,
  SYMPTOM_FINE,
  allSymptomKeys,
  groupKeys,
  impliedBy,
  legacyOf,
  type ChoiceGroupId,
} from "../domain/symptoms";
import type { Contraception, CycleState, DoseStep, JointKey, PhaseIndex } from "../domain/types";
import { touchDay } from "./dayLog";
import type { NoticedWalk } from "../domain/noticedWalks";
import { walkTotals } from "../domain/walk";
import type { WalkLog } from "../domain/types";

export { SYMPTOM_FINE };

/**
 * The three collections a user can both add to and take back.
 *
 * Every write goes through here rather than touching the maps directly, because each one has to
 * stamp *when* it happened: the offline merge resolves an add against a removal by comparing
 * those stamps, and a removal that forgets to leave a tombstone is silently undone by the next
 * sync. That was the travel-mode bug — see sync/merge.ts. (ADR-019)
 *
 * These mutate an immer draft, which is why they live in `store/` and not in `domain/`.
 */

/**
 * Picks or un-picks one of a day's symptoms, stamping either way so the merge can tell a
 * deselection from a device that never had it. "Feeling fine" is exclusive, and the symptoms it
 * clears are tombstoned like any other removal, or the older copy on the server would bring them
 * straight back. (ADR-019)
 */
export function setSymptom(d: KayaState, ds: string, key: string, on: boolean, now = Date.now()): void {
  const L = touchDay(d, ds);
  const at = (L.symptomsAt = L.symptomsAt || {});
  const off = (L.symptomsOff = L.symptomsOff || {});
  let cur = L.symptoms || [];
  const takeBack = (k: string) => {
    cur = cur.filter((x) => x !== k);
    delete at[k];
    off[k] = now;
  };
  if (!on) takeBack(key);
  else {
    for (const k of cur) if (k !== key && (key === SYMPTOM_FINE || k === SYMPTOM_FINE)) takeBack(k);
    if (!cur.includes(key)) cur = [...cur, key];
    at[key] = now;
    delete off[key];
  }
  L.symptoms = cur;
}

/**
 * Answers one single-choice group of the period-day sheet (`flow`, `pain`, `energy`, `mood`,
 * `sleep`), or clears it with null.
 *
 * Every sibling is tombstoned, not only the one this phone holds: another phone may have picked
 * a different answer offline, and only a removal stamped here can outrank it in the merge — or
 * the day would come back with two flows. The legacy key the answer implies (`pain:2` ⇒ `cramps`)
 * is written alongside it, so an older build's sheet and every existing tally still read it, and
 * taken back when the answer stops implying it. (ADR-019, invariant 3)
 */
export function setSymptomChoice(
  d: KayaState,
  ds: string,
  group: ChoiceGroupId,
  value: string | null,
  now = Date.now()
): void {
  const L = touchDay(d, ds);
  const at = (L.symptomsAt = L.symptomsAt || {});
  const off = (L.symptomsOff = L.symptomsOff || {});
  const chosen = value === null ? null : `${group}:${value}`;
  const legacy = legacyOf(group);
  const implied = impliedBy(group, value);

  const drop = new Set(groupKeys(group).filter((k) => k !== chosen));
  if (legacy && legacy !== implied) drop.add(legacy);
  // "soaked through" only means anything alongside heavy flow
  if (group === "flow" && value !== "heavy") drop.add(SOAKED);
  if (chosen) drop.add(SYMPTOM_FINE);

  let cur = (L.symptoms || []).filter((k) => !drop.has(k));
  for (const k of drop) {
    delete at[k];
    off[k] = now;
  }
  for (const k of [chosen, implied]) {
    if (!k) continue;
    if (!cur.includes(k)) cur = [...cur, k];
    at[k] = now;
    delete off[k];
  }
  L.symptoms = cur;
}

/**
 * "Feeling fine": sets `fine` and takes back every other answer the vocabulary knows, present on
 * this phone or not, so an answer another phone logged offline cannot come back through it.
 */
export function clearSymptoms(d: KayaState, ds: string, now = Date.now()): void {
  const L = touchDay(d, ds);
  const at = (L.symptomsAt = L.symptomsAt || {});
  const off = (L.symptomsOff = L.symptomsOff || {});
  for (const k of new Set([...allSymptomKeys(), ...(L.symptoms || [])])) {
    if (k === SYMPTOM_FINE) continue;
    delete at[k];
    off[k] = now;
  }
  L.symptoms = [SYMPTOM_FINE];
  at[SYMPTOM_FINE] = now;
  delete off[SYMPTOM_FINE];
}

/**
 * The cycle settings block — length, period length, contraception — is merged as one unit by its
 * `settingsAt` stamp, so each writer has to stamp it. (ADR-033)
 *
 * Only a real change stamps. Every settings screen saves through the shared profile form, which
 * writes these numbers back unchanged; stamping that would let a Body & goals save on a phone
 * that never saw another phone's contraception answer out-stamp it and drop it on the next sync.
 */
export function setCycleSettings(
  d: KayaState,
  patch: Partial<Pick<CycleState, "length" | "periodLen" | "contraception">>,
  now = Date.now()
): void {
  let changed = false;
  if (patch.length !== undefined && patch.length !== d.cycle.length) {
    d.cycle.length = patch.length;
    changed = true;
  }
  if (patch.periodLen !== undefined && patch.periodLen !== d.cycle.periodLen) {
    d.cycle.periodLen = patch.periodLen;
    changed = true;
  }
  if (patch.contraception !== undefined && patch.contraception !== d.cycle.contraception) {
    d.cycle.contraception = patch.contraception;
    changed = true;
  }
  if (changed) d.cycle.settingsAt = now;
}

export function setContraception(d: KayaState, value: Contraception, now = Date.now()): void {
  setCycleSettings(d, { contraception: value }, now);
}

export function setTravelDay(d: KayaState, ds: string, on: boolean, now = Date.now()): void {
  d.travelAt = d.travelAt || {};
  d.travelOff = d.travelOff || {};
  if (on) {
    d.travelDates[ds] = 1;
    d.travelAt[ds] = now;
    delete d.travelOff[ds];
    // A travel day and a hand-picked session are two answers to the same question.
    setSessionOverride(d, ds, null, now);
  } else {
    delete d.travelDates[ds];
    delete d.travelAt[ds];
    d.travelOff[ds] = now;
  }
}

export function setSessionOverride(
  d: KayaState,
  ds: string,
  key: SessionKey | null,
  now = Date.now()
): void {
  d.overridesAt = d.overridesAt || {};
  d.overridesOff = d.overridesOff || {};
  if (key) {
    d.overrides[ds] = key;
    d.overridesAt[ds] = now;
    delete d.overridesOff[ds];
  } else {
    delete d.overrides[ds];
    delete d.overridesAt[ds];
    d.overridesOff[ds] = now;
  }
}

/**
 * "Keep my session" on a period day: the weekday's own session, held for the day as an override
 * so the period swap stays out of it after a sync (ADR-057). Nothing to keep on a rest weekday.
 */
export function keepMySession(d: KayaState, ds: string, now = Date.now()): void {
  const k = gentleOffKey(d, ds);
  if (k) setSessionOverride(d, ds, k, now);
}

export function setPeriodStart(d: KayaState, ds: string, on: boolean, now = Date.now()): void {
  d.cycle.startsAt = d.cycle.startsAt || {};
  d.cycle.startsOff = d.cycle.startsOff || {};
  if (on) {
    if (!d.cycle.starts.includes(ds)) d.cycle.starts.push(ds);
    d.cycle.starts.sort();
    d.cycle.startsAt[ds] = now;
    delete d.cycle.startsOff[ds];
    setSessionOverride(d, ds, null, now);
  } else {
    d.cycle.starts = d.cycle.starts.filter((x) => x !== ds);
    delete d.cycle.startsAt[ds];
    d.cycle.startsOff[ds] = now;
    // The end date belonged to the start being taken back; leaving it would have the next
    // period read as having ended before it began.
    if (d.cycle.ends) delete d.cycle.ends[ds];
  }
}

/**
 * A rescue or extra session has finished: its running clock joins the day's banked total and is
 * zeroed, and the zeroing is stamped. Under the merge's plain larger-clock rule a copy taken
 * mid-session came straight back on the first sync after a finish, so the next extra of the day
 * started from the finished one's time and banked and priced it a second time. Returns the clock
 * it banked, for the kcal and the recap. (ADR-034)
 */
export function bankExtraClock(d: KayaState, ds: string, now = Date.now()): number {
  const L = touchDay(d, ds);
  const ms = L.extraElapsed || 0;
  L.extraDoneMs = (L.extraDoneMs || 0) + ms;
  L.extraElapsed = 0;
  L.extraElapsedAt = now;
  return ms;
}

/**
 * Adds something eaten to a day.
 *
 * Entries carry their own id and are appended rather than replacing the array, so two phones can
 * both log lunch while offline and neither loses it — the merge unions by id. (ADR-019, ADR-025)
 */
export function addFoodEntry(d: KayaState, ds: string, entry: FoodEntry, now = new Date()): void {
  const L = touchDay(d, ds);
  L.food = [...(L.food || []), entry];
  if (L.foodOff) delete L.foodOff[entry.id];
  // Today's targets ride along with every entry, so the last meal logged leaves the day with the
  // targets it was eaten against (ADR-040). A past day keeps whatever it has: re-judging it by
  // today's weight or today's "periods have stopped" is exactly what the snapshot is for.
  if (ds === todayStr(now)) L.targets = freezeTargets(d, now);
}

/** Removes one, leaving a tombstone — without it the next sync puts the meal back. */
export function removeFoodEntry(d: KayaState, ds: string, id: string, now = Date.now()): void {
  const L = d.logs[ds];
  if (!L) return;
  L.food = (L.food || []).filter((e) => e.id !== id);
  L.foodOff = { ...(L.foodOff || {}), [id]: now };
}

/* ---------- the meal plan (ADR-026) ----------
   The plan is a second copy of the food log's shape — items by id, removals stamped — so it gets
   the food log's writers and the food log's merge. Nothing else may write `mealPlan`. */

function newId(now: number): string {
  return `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function plan(d: KayaState): MealPlan {
  if (!d.mealPlan) d.mealPlan = {};
  return d.mealPlan;
}

function planDay(d: KayaState, ds: string): MealPlanDay {
  const p = plan(d);
  p.days = p.days || {};
  if (!p.days[ds]) p.days[ds] = {};
  return p.days[ds];
}

/**
 * The id to write under a deterministic `base`, or null when it is already there.
 *
 * Deterministic ids are what make an offline double tap — or the same tap on two phones — land as
 * one item after the merge. But a tombstoned id can never live again (the merge lets any
 * tombstone win), so once `base` has been taken back the next write takes `base:2`, then `base:3`.
 */
function slotFor(base: string, live: Set<string>, dead: Record<string, number> | undefined): string | null {
  const isBase = (id: string) => id === base || id.startsWith(`${base}:`);
  if ([...live].some(isBase)) return null;
  if (!dead || !(base in dead)) return base;
  let n = 2;
  while (`${base}:${n}` in dead) n++;
  return `${base}:${n}`;
}

export function addPlanItem(d: KayaState, ds: string, item: PlanItem): void {
  const day = planDay(d, ds);
  day.items = [...(day.items || []), item];
}

export function removePlanItem(d: KayaState, ds: string, id: string, now = Date.now()): void {
  const day = d.mealPlan?.days?.[ds];
  if (!day) return;
  day.items = (day.items || []).filter((e) => e.id !== id);
  day.off = { ...(day.off || {}), [id]: now };
}

/** Saves a named set of items as a template, replacing one with the same id. Items are copied
 *  with fresh ids so a template never shares an id with the day it was taken from. Returns the
 *  template's id. */
export function saveTemplate(
  d: KayaState,
  tpl: { id?: string; name: string; items: PlanItem[] },
  now = Date.now()
): string {
  const p = plan(d);
  const id = tpl.id ?? newId(now);
  p.templates = p.templates || {};
  p.templatesAt = p.templatesAt || {};
  p.templatesOff = p.templatesOff || {};
  p.templates[id] = {
    id,
    name: tpl.name.trim() || "Saved day",
    // Stamped into the item ids so a re-saved template applies as new items rather than being
    // mistaken for the version already applied to a day.
    items: tpl.items.map((e, i) => ({ ...e, id: `${now.toString(36)}.${i}`, at: now + i })),
    at: now,
  };
  p.templatesAt[id] = now;
  delete p.templatesOff[id];
  return id;
}

export function removeTemplate(d: KayaState, id: string, now = Date.now()): void {
  const p = plan(d);
  p.templatesAt = p.templatesAt || {};
  p.templatesOff = p.templatesOff || {};
  if (p.templates) delete p.templates[id];
  delete p.templatesAt[id];
  p.templatesOff[id] = now;
}

/**
 * Puts a template's items into a day's plan. Ids are `t:<tpl>:<item>:<day>`, so applying it twice
 * — or on two phones — plans the day once. Returns how many items were added.
 */
export function applyTemplate(d: KayaState, ds: string, tplId: string, now = Date.now()): number {
  const tpl = d.mealPlan?.templates?.[tplId];
  if (!tpl) return 0;
  const day = planDay(d, ds);
  const live = new Set((day.items || []).filter((e) => !(day.off && e.id in day.off)).map((e) => e.id));
  let added = 0;
  tpl.items.forEach((e, i) => {
    const id = slotFor(`t:${tplId}:${e.id}:${ds}`, live, day.off);
    if (!id) return;
    addPlanItem(d, ds, { ...e, id, at: now + i });
    live.add(id);
    added++;
  });
  return added;
}

/**
 * Logs a planned item as eaten, values copied as they stand in the plan.
 *
 * The entry's id is derived from the plan item's (`p:<id>`) so a double tap, or the same tap on
 * two offline phones, is one entry after the merge. Un-logging tombstones that id for good, so a
 * re-log takes `p:<id>:2` and so on. Returns false when it was already logged.
 */
export function logPlanned(d: KayaState, ds: string, itemId: string, now = Date.now()): boolean {
  const item = planFor(d, ds).find((e) => e.id === itemId);
  if (!item || isLogged(d, ds, itemId)) return false;
  const live = new Set(entriesFor(d, ds).map((e) => e.id));
  const id = slotFor(loggedIdFor(itemId), live, d.logs[ds]?.foodOff);
  if (!id) return false;
  addFoodEntry(d, ds, { ...item, id, at: now });
  return true;
}

/** "Copy yesterday": every live entry of `fromDs` logged again on `toDs`, as new entries. */
export function copyDayFood(d: KayaState, fromDs: string, toDs: string, now = Date.now()): number {
  const src = entriesFor(d, fromDs);
  src.forEach((e, i) => addFoodEntry(d, toDs, { ...e, id: newId(now + i), at: now + i }));
  return src.length;
}

/** "Repeat this meal": one meal slot of `fromDs` logged again on `toDs`. */
export function repeatMeal(
  d: KayaState,
  fromDs: string,
  meal: MealSlot,
  toDs: string,
  now = Date.now()
): number {
  const src = entriesFor(d, fromDs).filter((e) => e.meal === meal);
  src.forEach((e, i) => addFoodEntry(d, toDs, { ...e, id: newId(now + i), at: now + i }));
  return src.length;
}

/** Swaps a day's planned items for a scaled copy — "fit this meal to what is left". The old
 *  items are tombstoned and the new ones get new ids, because plan items are immutable. */
export function replacePlanItems(
  d: KayaState,
  ds: string,
  oldIds: string[],
  items: PlanItem[],
  now = Date.now()
): void {
  for (const id of oldIds) removePlanItem(d, ds, id, now);
  items.forEach((e, i) => addPlanItem(d, ds, { ...e, id: newId(now + i), at: now + i }));
}

/* ---------- the planner's writes (ADR-043) ----------
   Generated items arrive with derived base ids (`g:<day>:<slot>:<n>`); each is written under the
   first free slot of its base, so the same plan made on two offline phones lands once, and a
   regenerate — which tombstones the old ids for good — takes `:2`, `:3`. Only generated ids are
   ever tombstoned here: an item the person placed is never replaced by the planner. */

/** Writes a generated day's items. Returns how many were written. */
export function writeGenerated(d: KayaState, ds: string, items: PlanItem[]): number {
  const day = planDay(d, ds);
  const live = new Set((day.items || []).filter((e) => !(day.off && e.id in day.off)).map((e) => e.id));
  let added = 0;
  for (const e of items) {
    const id = slotFor(e.id, live, day.off);
    if (!id) continue;
    addPlanItem(d, ds, { ...e, id });
    live.add(id);
    added++;
  }
  return added;
}

/** Tombstones a day's generated items — one meal's, or all of them — leaving the person's own. */
export function clearGenerated(d: KayaState, ds: string, meal?: MealSlot, now = Date.now()): number {
  const gone = planFor(d, ds).filter((e) => e.id.startsWith("g:") && (!meal || e.meal === meal));
  for (const e of gone) removePlanItem(d, ds, e.id, now);
  return gone.length;
}

/** "Regenerate": the meal's generated items out, the new ones in under suffixed ids. */
export function regenerateMeal(d: KayaState, ds: string, meal: MealSlot, items: PlanItem[], now = Date.now()): number {
  clearGenerated(d, ds, meal, now);
  return writeGenerated(
    d,
    ds,
    items.filter((e) => e.meal === meal)
  );
}

/**
 * "Copy this day to tomorrow" and "Copy yesterday's food into this day": `items` — a planned day
 * or a logged one — become the target day's plan, meal by meal. A meal where the person already
 * placed something of their own is left alone; generated items in a meal the copy fills are
 * tombstoned. Copies take new ids: they are the person's now, and no regenerate touches them.
 * Returns how many were copied.
 */
export function copyIntoPlan(d: KayaState, items: FoodEntry[], toDs: string, now = Date.now()): number {
  const there = planFor(d, toDs);
  const own = new Set(there.filter((e) => !e.id.startsWith("g:")).map((e) => e.meal));
  const meals = new Set(items.map((e) => e.meal).filter((m) => !own.has(m)));
  for (const m of meals) clearGenerated(d, toDs, m, now);
  let i = 0;
  for (const e of items) {
    if (!meals.has(e.meal)) continue;
    addPlanItem(d, toDs, { ...e, id: newId(now + i), at: now + i });
    i++;
  }
  return i;
}

/**
 * One logged entry copied to another day and meal (FoodEntryMove): a day still to come gets it as
 * a planned item, today or a past day as eaten. A new id either way — the copy is its own item.
 */
export function copyEntryTo(
  d: KayaState,
  entry: FoodEntry,
  toDs: string,
  meal: MealSlot,
  today: string,
  now = Date.now()
): void {
  const copy: FoodEntry = { ...entry, id: newId(now), at: now, meal };
  if (toDs > today) addPlanItem(d, toDs, copy);
  else addFoodEntry(d, toDs, copy, new Date(now));
}

/**
 * "Move to another meal": the same portion under a new meal — an edit, so a tombstone plus a new
 * id (ADR-026). An entry logged from the plan keeps its `p:` stem so the plan still counts it.
 * Returns false when the entry is gone or already in that meal.
 */
export function moveEntryMeal(d: KayaState, ds: string, id: string, meal: MealSlot, now = Date.now()): boolean {
  const e = entriesFor(d, ds).find((x) => x.id === id);
  if (!e || e.meal === meal) return false;
  const L = d.logs[ds];
  const taken = new Set([...(L?.food || []).map((x) => x.id), ...Object.keys(L?.foodOff || {})]);
  const stem = planFor(d, ds)
    .map((p) => loggedIdFor(p.id))
    .filter((b) => id === b || id.startsWith(`${b}:`))
    .sort((a, b) => b.length - a.length)[0];
  let next = `${now.toString(36)}-m`;
  let k = 2;
  if (stem) {
    while (taken.has(`${stem}:${k}`)) k++;
    next = `${stem}:${k}`;
  } else {
    while (taken.has(next)) next = `${now.toString(36)}-m${k++}`;
  }
  removeFoodEntry(d, ds, id, now);
  addFoodEntry(d, ds, { ...e, id: next, meal }, new Date(now));
  return true;
}

/* ---------- the user's own foods ----------
   The food log's shape again — immutable items by id, removals stamped — so the same merge. An
   edit is a removal plus a new id; entries already logged from the old version keep the values
   they copied (invariant 18), so nothing else is touched. */

/** What the user supplies for a custom food; the id and stamp are minted here. */
export type CustomFoodInput = Omit<CustomFood, "id" | "at">;

function customId(now: number): string {
  return `cf:${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Adds one and returns its id. */
export function addCustomFood(d: KayaState, food: CustomFoodInput, now = Date.now()): string {
  const id = customId(now);
  d.customFoods = [
    ...(d.customFoods || []),
    { ...food, name: food.name.trim(), per100: { ...food.per100 }, id, at: now },
  ];
  return id;
}

/** Takes one back, leaving a tombstone — without it the next sync puts it back. */
export function removeCustomFood(d: KayaState, id: string, now = Date.now()): void {
  d.customFoods = (d.customFoods || []).filter((f) => f.id !== id);
  d.customFoodsOff = { ...(d.customFoodsOff || {}), [id]: now };
}

/**
 * Replaces one with an edited copy under a new id, and returns that id — null when `id` is not a
 * live custom food. A `per100` in the patch replaces the old one whole, so a value the user
 * cleared is absent rather than left over.
 */
export function editCustomFood(
  d: KayaState,
  id: string,
  patch: Partial<CustomFoodInput>,
  now = Date.now()
): string | null {
  const old = (d.customFoods || []).find((f) => f.id === id);
  if (!old || (d.customFoodsOff && id in d.customFoodsOff)) return null;
  removeCustomFood(d, id, now);
  // A dish keeps its ingredients and pot unless the patch brings new ones (ADR-048).
  const base: CustomFoodInput = {
    name: old.name,
    unit: old.unit,
    g: old.g,
    per100: old.per100,
    ...(old.dish ? { dish: old.dish } : {}),
  };
  return addCustomFood(d, { ...base, ...patch }, now);
}

/* ---------- the day's marks (ADR-049) ----------
   Untracked and fast day are date sets that can be switched off again, so they follow the travel
   days' rule: every add and every removal stamped, and the merge keeps a date only when its last
   add out-stamps its last removal. A day carries one mark at most — both answer why the day does
   not count — so setting one takes the other back, stamped like any removal, or the other phone's
   copy would bring it back. */

type MarkSet = { dates: "untrackedDates" | "fastDates"; at: "untrackedAt" | "fastAt"; off: "untrackedOff" | "fastOff" };
const UNTRACKED: MarkSet = { dates: "untrackedDates", at: "untrackedAt", off: "untrackedOff" };
const FAST: MarkSet = { dates: "fastDates", at: "fastAt", off: "fastOff" };

function setMark(d: KayaState, m: MarkSet, ds: string, on: boolean, now: number): void {
  const dates = (d[m.dates] = d[m.dates] || {});
  const at = (d[m.at] = d[m.at] || {});
  const off = (d[m.off] = d[m.off] || {});
  if (on) {
    dates[ds] = 1;
    at[ds] = now;
    delete off[ds];
  } else {
    // Only a mark that is there, or that some copy may still hold, needs a tombstone; but writing
    // one for a date never marked is harmless, and cheaper than asking which copies exist.
    delete dates[ds];
    delete at[ds];
    off[ds] = now;
  }
}

/** "Untracked today": the day is kept out of every average; what was logged stays. */
export function setUntrackedDay(d: KayaState, ds: string, on: boolean, now = Date.now()): void {
  if (on) setMark(d, FAST, ds, false, now);
  setMark(d, UNTRACKED, ds, on, now);
}

/** "Fast day": no deficit, kept out of the averages, never a missed day. */
export function setFastDay(d: KayaState, ds: string, on: boolean, now = Date.now()): void {
  if (on) setMark(d, UNTRACKED, ds, false, now);
  setMark(d, FAST, ds, on, now);
}

/* ---------- water (ADR-044) ----------
   The food log's shape once more — immutable drinks by id, removals stamped — so the same merge.
   The settings are one stamped block, like the cycle settings. */

/** A drink ready to log. The id is minted here so the page can offer Undo on exactly this one. */
export function waterEntry(
  input: { ml: number; kind: WaterKind; note?: string },
  now = Date.now()
): WaterEntry {
  return {
    id: `w:${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    at: now,
    ml: Math.round(input.ml),
    kind: input.kind,
    ...(input.note ? { note: input.note } : {}),
  };
}

export function addWater(d: KayaState, ds: string, entry: WaterEntry): void {
  const L = touchDay(d, ds);
  L.water = [...(L.water || []), entry];
  if (L.waterOff) delete L.waterOff[entry.id];
}

/** Takes one back, leaving a tombstone — without it the next sync puts the glass back. */
export function removeWater(d: KayaState, ds: string, id: string, now = Date.now()): void {
  const L = d.logs[ds];
  if (!L) return;
  L.water = (L.water || []).filter((e) => e.id !== id);
  L.waterOff = { ...(L.waterOff || {}), [id]: now };
}

/**
 * Folds in drinks logged from a notification — the worker's own queue and the server's inbox
 * (ADR-045). Each goes in once, by its id, and never over its own tombstone: a glass taken back
 * on the Water page must not return because the inbox still held it.
 */
export function applyDrinks(d: KayaState, drinks: PendingDrink[]): void {
  for (const { ds, ...e } of drinks) if (drinkIsNew(d, { ds, ...e })) addWater(d, ds, e);
}

/** The hot-day switch, stamped either way so switching it off survives a copy taken while on. */
export function setHotDay(d: KayaState, ds: string, on: boolean, now = Date.now()): void {
  const L = touchDay(d, ds);
  if (on) L.hot = true;
  else delete L.hot;
  L.hotAt = now;
}

/**
 * Changes the water settings, stamping the block only when something actually changed — the
 * setCycleSettings rule: a save that writes the same values back must not out-stamp another
 * phone's real change.
 */
export function setWaterSettings(
  d: KayaState,
  patch: Partial<Omit<WaterSettings, "at">>,
  now = Date.now()
): void {
  const cur: WaterSettings = { ...WATER_DEFAULTS, ...(d.profile.water || {}) };
  const next: WaterSettings = { ...cur, ...patch };
  // an answer taken back is absent, not undefined, so the block stays plain JSON
  for (const k of ["limitMl", "diuretic", "times"] as const) if (next[k] === undefined) delete next[k];
  const same = (Object.keys({ ...cur, ...next }) as (keyof WaterSettings)[]).every(
    (k) => k === "at" || JSON.stringify(cur[k]) === JSON.stringify(next[k])
  );
  if (same) return;
  d.profile.water = { ...next, at: now };
}

/* ---------- the eating answers (ADR-042) ---------- */

const EATING_LISTS = ["avoid", "dislikes"] as const;

/**
 * Saves the questionnaire's answers as one block. Only a real change stamps it — the water rule —
 * so re-saving the same answers from a phone that never saw another's change cannot out-stamp it.
 *
 * `avoid` and `dislikes` also stamp each key: an add stamps its `…At`, a removal leaves a
 * tombstone in `…Off`, because the merge resolves those two lists key by key. An answer left out
 * of `answers` is taken back to "not asked" — the questionnaire always writes every screen it
 * showed, so that happens only for a question this person is no longer asked.
 */
export function setEatingProfile(d: KayaState, answers: EatingAnswers, now = Date.now()): boolean {
  const cur: EatingProfile = d.profile.eating || {};
  const next: EatingProfile = {};
  for (const [k, v] of Object.entries(answers) as [keyof EatingAnswers, unknown][]) {
    if (v !== undefined) (next as Record<string, unknown>)[k] = v;
  }
  const answersOf = (e: EatingProfile): string => {
    const o: Record<string, unknown> = { ...e };
    for (const k of ["at", "avoidAt", "avoidOff", "dislikesAt", "dislikesOff"]) delete o[k];
    return JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k]]));
  };
  if (cur.at !== undefined && answersOf(cur) === answersOf(next)) return false;

  for (const key of EATING_LISTS) {
    const atKey = `${key}At` as const;
    const offKey = `${key}Off` as const;
    const was = new Set<string>(cur[key] || []);
    const kept = new Set<string>(next[key] || []);
    const at = { ...(cur[atKey] || {}) };
    const off = { ...(cur[offKey] || {}) };
    for (const k of kept) {
      if (!was.has(k) || at[k] === undefined) at[k] = now;
      delete off[k];
    }
    for (const k of was) {
      if (kept.has(k)) continue;
      off[k] = now;
      delete at[k];
    }
    if (Object.keys(at).length) next[atKey] = at;
    if (Object.keys(off).length) next[offKey] = off;
  }
  next.at = now;
  d.profile.eating = next;
  return true;
}

/* ---------- the "Log dinner?" reminder (ADR-046) ---------- */

/**
 * Switches the dinner nudge or moves its time, stamping the pair only when it actually changed —
 * the water block's rule. The stamp is what lets a linked account set it from another phone: an
 * unstamped profile scalar follows whichever phone synced last, and the parent's phone, which
 * never saw the change, would hand back its own copy.
 */
export function setDinnerReminder(
  d: KayaState,
  patch: { on?: boolean; at?: string },
  now = Date.now()
): void {
  const p = d.profile;
  const on = patch.on ?? p.dinnerRemindOn;
  const at = patch.at ?? p.dinnerRemindAt;
  if (on === p.dinnerRemindOn && at === p.dinnerRemindAt) return;
  if (on !== undefined) p.dinnerRemindOn = on;
  if (at !== undefined) p.dinnerRemindAt = at;
  p.dinnerRemindSetAt = now;
}

/* ---------- what a day ran, ticks and their clear (ADR-050) ---------- */

/**
 * Freezes what the day's own session runs, at its first tick: the key, the phase, hip-safe and the
 * queue's signature. Every reader used to rebuild the queue from today's settings, so a phase that
 * moved in week 3 or hip-safe switched mid-week re-read the ticks against another list. Never
 * overwritten while that session has a tick — a block the merge or a clear left without ticks is
 * replaced. `from` is the block the player froze at Start, so a run resumed after the phase moved
 * keeps the queue it began. Called by the player before each tick of the day's own session; the
 * rescue and the extras keep their own buckets and never freeze the day.
 */
export function markRan(d: KayaState, ds: string, key: SessionKey, from?: DayRan, now = Date.now()): void {
  const L = touchDay(d, ds);
  const cur = L.ran;
  if (cur && Object.keys(L.done[cur.key] || {}).length > 0) return;
  const base = from && from.key === key ? from : ranFor(d, key, now);
  L.ran = { ...base, at: now };
}

/** Ticks one step, stamping the bucket so the merge can tell a tick from a cleared one. */
export function tickStep(d: KayaState, ds: string, bucket: string, idx: number, now = Date.now()): void {
  const L = touchDay(d, ds);
  if (!L.done[bucket]) L.done[bucket] = {};
  L.done[bucket][idx] = 1;
  if (!L.doneAt) L.doneAt = {};
  L.doneAt[bucket] = now;
}

/**
 * Takes back the ticks from `from` on — Previous redoes a move, and everything after it with it.
 * Not a tombstone: the bucket's earlier ticks stand, and a copy holding the later ones can bring
 * them back on a sync, as it always could.
 */
export function untickFrom(d: KayaState, ds: string, bucket: string, from: number): void {
  const map = touchDay(d, ds).done[bucket];
  if (!map) return;
  for (const k of Object.keys(map)) if (+k >= from) delete map[+k];
}

/**
 * "Clear today's progress": the session goes back to not started, and the clear is stamped in
 * `doneOff`. A plain union of `done` brought the ticks back from any phone that still held them;
 * the merge now keeps a bucket's ticks only when they are newer than its clear, and drops the
 * finish, the clock and the frozen block of a copy taken before it. (ADR-050)
 */
export function clearProgress(d: KayaState, ds: string, key: SessionKey, now = Date.now()): void {
  const L = touchDay(d, ds);
  delete L.done[key];
  if (L.doneAt) {
    delete L.doneAt[key];
    if (!Object.keys(L.doneAt).length) delete L.doneAt;
  }
  if (!L.doneOff) L.doneOff = {};
  L.doneOff[key] = now;
  delete L.completedAt;
  delete L.kcal;
  delete L.elapsedMs;
  if (L.ran && L.ran.key === key) delete L.ran;
}

/* ---------- after a session, the self-tests, the exercise answers (ADR-050) ---------- */

/** "How was it?" — one answer a day, the later one whole. */
export function setHowWas(d: KayaState, ds: string, v: Omit<HowWas, "at">, now = Date.now()): void {
  const out: HowWas = { fit: v.fit, at: now };
  if (v.pain) out.pain = { joint: v.pain.joint, score: v.pain.score };
  if (v.key) out.key = v.key;
  if (v.short) out.short = true;
  if (v.talk) out.talk = v.talk;
  touchDay(d, ds).howWas = out;
}

/** The morning after (ADR-065): how yesterday's sore joint is today, on today's log — the day it
 *  was answered — so the session day's own answer stays as it was given. One a day, the later
 *  answer whole. */
export function setMorningAfter(d: KayaState, today: string, ds: string, joint: JointKey, ok: boolean, now = Date.now()): void {
  touchDay(d, today).morning = { ds, joint, ok, at: now };
}

/** The day's test results. Tests taken one after another on the same phone add up into one block;
 *  across phones the later block wins whole. An `undefined` takes a figure back. */
export function setTests(d: KayaState, ds: string, v: Omit<DayTests, "at">, now = Date.now()): void {
  const L = touchDay(d, ds);
  const out: Record<string, unknown> = { ...(L.tests || {}) };
  // `by` names whoever wrote this change: a count the person takes on their own phone after a
  // linked owner typed one is theirs (ADR-056)
  delete out.by;
  for (const [k, x] of Object.entries(v)) {
    if (x === undefined) delete out[k];
    else out[k] = x;
  }
  out.at = now;
  L.tests = out as unknown as DayTests;
}

/** A template's dose step after "How was it?" (ADR-054). Rides the training block's stamp, and
 *  stamps `doseAt` so the run of answers that moves it next counts from here. */
export function setDoseStep(d: KayaState, key: SessionKey, step: DoseStep, now = Date.now()): void {
  const t = d.profile.training || {};
  setTrainingProfile(d, { doseStep: { ...(t.doseStep || {}), [key]: step }, doseAt: { ...(t.doseAt || {}), [key]: now } }, now);
}

/** "Too hard" twice on the Kaya programme: the phase pinned one lower, and the pin kept so Train
 *  can say so. Changing the phase back in You › Sessions is the reversal (ADR-054). */
export function pinPhase(d: KayaState, from: PhaseIndex, to: PhaseIndex, now = Date.now()): void {
  d.profile.phaseMode = String(to);
  setTrainingProfile(d, { phasePin: { from, to, at: now } }, now);
}

/**
 * Save "How was it?" and act on it (ADR-054): on the Steady programme the template's dose step
 * moves by `readiness.ts::nextDose`; on the Kaya programme "too hard" twice pins the phase one lower
 * (`phasePinFor`; `current` is `plan.ts::phase(S)`). Returns what changed, for the page's toast —
 * read it through a closure, since `update` drops a writer's return value.
 */
export function recordHowWas(
  d: KayaState,
  ds: string,
  v: Omit<HowWas, "at">,
  current: PhaseIndex,
  now = Date.now()
): { dose: DoseDecision | null; pin: PhasePinDecision | null } {
  setHowWas(d, ds, v, now);
  const when = new Date(now);
  if (programmeOf(d, when) === "steady") {
    if (!v.key) return { dose: null, pin: null };
    const dose = nextDose(d, v.key, ds, when);
    // only a move restarts the run of answers; "the same again" must keep counting towards two
    if (dose.step !== dose.from || dose.variant) setDoseStep(d, v.key, dose.step, now);
    return { dose, pin: null };
  }
  const pin = phasePinFor(d, current, when);
  if (pin) pinPhase(d, pin.from, pin.to, now);
  return { dose: null, pin };
}

/** JSON with sorted keys, so two answers that differ only in key order compare equal. */
function stable(v: unknown): string {
  return JSON.stringify(v, (_k, x: unknown) =>
    x && typeof x === "object" && !Array.isArray(x)
      ? Object.fromEntries(
          Object.keys(x as object)
            .sort()
            .map((k) => [k, (x as Record<string, unknown>)[k]])
        )
      : x
  );
}

const TRAINING_STAMPS = ["at", "haveAt", "haveOff", "jointsAt", "jointsOff"] as const;

/**
 * Patches the exercise answers. A key present in `patch` with `undefined` takes that answer back to
 * "not asked". Stamps the block only when an answer actually changed, so a Save that changed
 * nothing cannot out-rank another phone's real change. `have` and `joints` also stamp each key — an
 * add or a changed answer its `…At`, a removal a tombstone in `…Off` — because the merge resolves
 * those two key by key, as it does `eating.avoid`. A linked owner writes through here too
 * (ADR-046), with `setBy`.
 */
export function setTrainingProfile(d: KayaState, patch: TrainingAnswers, now = Date.now()): boolean {
  const cur: TrainingProfile = d.profile.training || {};
  const next: Record<string, unknown> = { ...cur };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete next[k];
    else next[k] = v;
  }
  const answersOf = (t: Record<string, unknown>): string => {
    const o: Record<string, unknown> = { ...t };
    for (const k of TRAINING_STAMPS) delete o[k];
    return stable(o);
  };
  if (cur.at !== undefined && answersOf({ ...cur }) === answersOf(next)) return false;
  const out = next as TrainingProfile;

  const have = stampKeys(cur.have || [], out.have || [], cur.haveAt, cur.haveOff, () => false, now);
  if (have.at) out.haveAt = have.at;
  else delete out.haveAt;
  if (have.off) out.haveOff = have.off;
  else delete out.haveOff;

  const was: Record<string, unknown> = cur.joints || {};
  const kept: Record<string, unknown> = out.joints || {};
  const joints = stampKeys(
    Object.keys(was),
    Object.keys(kept),
    cur.jointsAt,
    cur.jointsOff,
    (k) => stable(was[k]) !== stable(kept[k]),
    now
  );
  if (joints.at) out.jointsAt = joints.at;
  else delete out.jointsAt;
  if (joints.off) out.jointsOff = joints.off;
  else delete out.jointsOff;

  out.at = now;
  d.profile.training = out;
  return true;
}

/** The add and removal stamps of a keyed answer after a save: a new or `changed` key is stamped
 *  now and loses any tombstone; a key that went is tombstoned now. Undefined when a map is empty. */
function stampKeys(
  before: readonly string[],
  after: readonly string[],
  atWas: Record<string, number> | undefined,
  offWas: Record<string, number> | undefined,
  changed: (k: string) => boolean,
  now: number
): { at?: Record<string, number>; off?: Record<string, number> } {
  const was = new Set(before);
  const kept = new Set(after);
  const at: Record<string, number> = { ...(atWas || {}) };
  const off: Record<string, number> = { ...(offWas || {}) };
  for (const k of kept) {
    if (!was.has(k) || at[k] === undefined || changed(k)) at[k] = now;
    delete off[k];
  }
  for (const k of was) {
    if (kept.has(k)) continue;
    off[k] = now;
    delete at[k];
  }
  return {
    at: Object.keys(at).length ? at : undefined,
    off: Object.keys(off).length ? off : undefined,
  };
}

/* ---------- walks (ADR-063) ---------- */

/**
 * Logs a walk the player timed. Keyed by its start, as the merge is (ADR-062): a second finish of
 * the same walk replaces it rather than adding another.
 */
export function addWalk(d: KayaState, ds: string, log: WalkLog, now = Date.now()): void {
  const L = touchDay(d, ds);
  const walks = (L.walks || []).filter((w) => w.startedAt !== log.startedAt);
  walks.push(log);
  walks.sort((x, y) => x.startedAt.localeCompare(y.startedAt));
  L.walks = walks;
}

/**
 * Saves a walk the counter noticed, on the person's tap only. Its steps are the samples' own, so it
 * is measured from the start — the sync's backfill leaves it alone — and its window is the
 * samples' ends, which is why it carries `noticed` and never a cadence word (ADR-063).
 */
export function addNoticedWalk(d: KayaState, ds: string, w: NoticedWalk, now = Date.now()): void {
  const t = walkTotals(d, w.secs, w.steps);
  addWalk(
    d,
    ds,
    {
      startedAt: w.startedAt,
      at: w.endedAt,
      secs: w.secs,
      kcal: t.kcal,
      measuredSteps: w.steps,
      ...(t.km !== null ? { km: t.km } : {}),
      noticed: true,
    },
    now
  );
}
